import axios, { AxiosProgressEvent, AxiosResponse } from 'axios'
import {
  Channel,
  CreateEntity,
  Identity,
  Maybe,
  MultiResourceResponse,
  Resource,
  ResourceTypes,
  SimpleMap,
  SingleResourceResponse,
} from '@vendorflow/types'
import { Message } from '@vendorflow/types/lib/types/Messages'
import { AZIMUTH_API_URL, VERSION } from 'constants/Api.constants'
import { convertResourceToEntity } from '@vendorflow/common/lib/converters/JsonApi.converter'
import qs from 'qs'
import { isEmpty, omit } from 'lodash'
import { AppendMessage, MessageWithContext } from 'types/Messages'
import { HIERARCHICAL_SEPARATOR } from 'constants/JsonApi.constants'
import { getRelationship } from 'services/Azimuth.service'
import { convertMessageToMessageWithContext } from 'converters/Messages.converters'
import {
  HandlingOptionsEnum,
  HandlingOptionsType,
  SwitchboardEndpointsEnum,
} from 'features/switchboard/Switchboard.types'
import { MessagesForChannelPageResult } from 'features/channels/Channels.types'
import { AUTHORIZATION_SENTINEL } from 'features/auth/Auth.constants'

export const BASE_URL = `${AZIMUTH_API_URL}/${VERSION}`

export async function fetchChannelsForIdentityUsingGET(identity: string, org: string): Promise<Channel[]> {
  const queryParams = qs.stringify({ filter: { workspace: org } })
  const result: AxiosResponse<MultiResourceResponse<Channel, ResourceTypes.CHANNELS>> = await axios.get(
    `${BASE_URL}/identities/${identity}/channels?${queryParams}`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function fetchChannelsForOrgUsingGET(orgId: string): Promise<Channel[]> {
  const result: AxiosResponse<MultiResourceResponse<Channel, ResourceTypes.CHANNELS>> = await axios.get(
    `${BASE_URL}/organizations/${orgId}/channels`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function fetchChannelUsingGET(
  channelId: string,
  includeMembers?: boolean,
  includeCaptiveChannelFor?: boolean
): Promise<{ channel: Channel; members: Identity[]; alice: Identity | null }> {
  const queryParams = qs.stringify({
    include: [includeMembers ? 'members' : undefined, includeCaptiveChannelFor ? 'captiveChannelFor' : undefined]
      .filter(Boolean)
      .join(','),
  })
  const result: AxiosResponse<SingleResourceResponse<Channel, ResourceTypes.CHANNELS>> = await axios.request({
    method: 'GET',
    url: `${BASE_URL}/channels/${channelId}${!isEmpty(queryParams) ? '?' + queryParams : ''}`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
  const channel: Channel = convertResourceToEntity(result.data.data)
  const includedMap =
    result.data.included?.reduce<SimpleMap<Resource<any, any>>>((acc, item) => {
      acc[`${item.type}::${item.id}`] = item
      return acc
    }, {}) || {}
  // @ts-ignore
  const memberResourceIds = getRelationship(result.data.data.relationships?.members).get()
  const members: Identity[] = memberResourceIds.map((member) => {
    return convertResourceToEntity(
      includedMap[`${member.type}::${member.id}`] as Resource<Identity, ResourceTypes.IDENTITIES>
    )
  })
  const { type: aliceType, id: aliceId } =
    getRelationship(result.data.data.relationships?.captiveChannelFor).getFirst() || {}
  const aliceResource = includedMap[`${aliceType}::${aliceId}`]
  const alice: Identity | null = aliceResource ? convertResourceToEntity(aliceResource) : null

  return { channel, members, alice }
}

export async function createChannelUsingPOST(orgId: string, channel: CreateEntity<Channel>): Promise<Channel> {
  const result: AxiosResponse<SingleResourceResponse<Channel, ResourceTypes.CHANNELS>> = await axios.post(
    `${BASE_URL}/organizations/${orgId}/channels`,
    {
      data: {
        type: ResourceTypes.CHANNELS,
        attributes: omit(channel, ['type']),
      },
    },
    { headers: { Authorization: AUTHORIZATION_SENTINEL } }
  )

  return convertResourceToEntity(result.data.data)
}

export async function addMembersToChannelUsingPOST(id: string, bobIds: string[]): Promise<void> {
  await axios.request({
    method: 'POST',
    url: `${BASE_URL}/channels/${id}/rel/members`,
    data: {
      data: bobIds.map((id) => ({ id, type: ResourceTypes.IDENTITIES })),
    },
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
}

export async function removeMembersFromChannelUsingDELETE(id: string, bobIds: string[]): Promise<void> {
  await axios.request({
    method: 'DELETE',
    url: `${BASE_URL}/channels/${id}/rel/members`,
    data: {
      data: bobIds.map((id) => ({ id, type: ResourceTypes.IDENTITIES })),
    },
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
}

export async function fetchMessageForChannelUsingGET(
  channelId: string,
  messageId: string
): Promise<MessageWithContext> {
  const result: AxiosResponse<SingleResourceResponse<Message, ResourceTypes.MESSAGES>> = await axios.request({
    method: 'GET',
    url: `${BASE_URL}/channels/${channelId}/messages/${messageId}`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return convertMessageToMessageWithContext(result.data.data)
}

export const MIN_SEARCH_LENGTH = 4

export async function fetchMessagesForChannelUsingGET({
  channelId,
  search,
  cursorHref,
  signal,
}: {
  channelId: string
  search?: Maybe<string>
  cursorHref?: string
  signal?: AbortSignal
}): Promise<MessagesForChannelPageResult> {
  const queryParams =
    search && search.length >= MIN_SEARCH_LENGTH ? qs.stringify({ containing: search.toLowerCase() }) : null
  const url = cursorHref || `${BASE_URL}/channels/${channelId}/messages${queryParams ? `?${queryParams}` : ''}`
  const result: AxiosResponse<MultiResourceResponse<Message, ResourceTypes.MESSAGES>> = await axios.request({
    url,
    method: 'GET',
    headers: { Authorization: AUTHORIZATION_SENTINEL },
    signal,
  })

  const data: MessageWithContext[] = result.data.data.map(convertMessageToMessageWithContext)
  const links = result.data?.links || {}
  const nextCursor = links.next?.href || null
  const futureCursor = links.updates?.href || links.future?.href || null

  return { data, nextCursor, futureCursor }
}

export async function sendMessageForChannelUsingPOST({
  message,
  handlingOptions,
  onUploadProgress,
}: {
  envy: string | null
  message: Omit<MessageWithContext, 'id' | 'type'>
  handlingOptions?: Maybe<HandlingOptionsType>
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
}): Promise<MessageWithContext> {
  const { body, channel, mentions, sender, thread, visibility } = message
  const payload: { data: AppendMessage } = {
    data: {
      type: ResourceTypes.MESSAGES,
      attributes: {
        body,
      },
      meta: {
        handlingOptions: {
          [HandlingOptionsEnum.PREFERRED_DELIVERY_ENDPOINTS]: handlingOptions?.[
            HandlingOptionsEnum.PREFERRED_DELIVERY_ENDPOINTS
          ] ?? [SwitchboardEndpointsEnum.SMS],
        },
      },
      relationships: {
        sender: {
          data: {
            id: sender,
            type: ResourceTypes.IDENTITIES,
          },
          links: {},
        },
      },
    },
  }

  if (handlingOptions?.[HandlingOptionsEnum.EGRESS_FROM_ADDRESSES]) {
    payload.data.meta.handlingOptions[HandlingOptionsEnum.EGRESS_FROM_ADDRESSES] =
      handlingOptions[HandlingOptionsEnum.EGRESS_FROM_ADDRESSES]
  }

  if (handlingOptions?.[HandlingOptionsEnum.RADAR_DRIVER]) {
    payload.data.meta.handlingOptions[HandlingOptionsEnum.RADAR_DRIVER] =
      handlingOptions[HandlingOptionsEnum.RADAR_DRIVER]
  }

  if (visibility) {
    payload.data.attributes.visibility = visibility
  }

  if (thread) {
    payload.data.relationships!.thread = {
      data: {
        id: thread,
        type: ResourceTypes.THREADS,
      },
      links: {},
    }
  }

  if (mentions && mentions.length) {
    payload.data.relationships!.mentions = {
      data: mentions.map((id) => ({ id, type: ResourceTypes.IDENTITIES })),
      links: {},
    }
  }

  const result: AxiosResponse<SingleResourceResponse<Message, ResourceTypes.MESSAGES>> = await axios.request({
    method: 'POST',
    url: `${BASE_URL}/channels/${channel}/messages`,
    data: payload,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
    onUploadProgress,
  })
  return convertMessageToMessageWithContext(result.data.data)
}

export async function fetchExternalChannelUsingGET(
  orgId: string,
  aliceId: string,
  copyMessages?: { channelId: string; messageIds: string[] }
): Promise<Channel> {
  const compositeIds =
    copyMessages?.messageIds.map((messageId) => copyMessages.channelId + HIERARCHICAL_SEPARATOR + messageId) || []

  const result: AxiosResponse<SingleResourceResponse<Channel, ResourceTypes.CHANNELS>> = await axios.request({
    method: 'GET',
    url: `${BASE_URL}/organizations/${orgId}/channels/external/${aliceId}${
      compositeIds.length > 0 ? `?${qs.stringify({ copyMessages: compositeIds }, { arrayFormat: 'repeat' })}` : ''
    }`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return convertResourceToEntity(result.data.data)
}
